/**
 * The plan vocabulary, mirrored from openwrench-intelligence-service's
 * `domain/vocabulary.py`, plus the helpers that read a plan.
 *
 * Upstream calls the vocabulary append-only: a name may be added but never
 * removed or redefined. Anything not in these tables is a plan Radar cannot
 * run, which is why the palette offers only what is here.
 */

import { v4 as uuidv4 } from 'uuid';
import {
	AOPCommunicateStep,
	AOPDecisionStep,
	AOPDraftPlan,
	AOPDraftStep,
	AOPEscalation,
	AOPExit,
	AOPFinishStep,
	AOPOperateStep,
	AOPOperation,
	AOPOutcome,
	AOPOutputDef,
	AOPPaletteGroup,
	AOPParty,
	AOPStep,
	AOPStepName,
	AOPStepType,
	AOPTrigger,
	AOP_AFTER_EXIT,
	AOP_ON_EXHAUSTED,
	AOP_ON_TIMEOUT,
	AOP_MAX_DESCRIPTION,
} from '../../../types/AOPTypes';

/**
 * What a card measures before it has rendered — a starting guess only. Layout
 * re-runs against the measured size as soon as React Flow has one, so neither
 * of these decides how big a card actually is: the content does, between the
 * bounds the card sets in CSS.
 */
/**
 * The number shown on a block: its position in `plan.steps`, counted from 1.
 *
 * Start carries no number, so these line up with the plan the service stores —
 * step 4 on a card is `plan.steps[3]` on the wire. The same number is used on
 * the cards, in the Steps panel, in the branch target pickers and in the create
 * drawer's review list, none of which draw a Start.
 */
export const stepNumberOf = (index: number): number => index + 1;

/** Re-exported so a caller reaching for the cap finds it beside the helpers. */
export { AOP_MAX_DESCRIPTION };

/** `label` is required and capped at 120 upstream; a question may run to 500. */
/**
 * Renaming a block. For a decision this writes the question and brings `label`
 * along, so anything reading the plan outside this canvas still sees a sensible
 * name on the step.
 */
export const renameStepPatch = (step: AOPDraftStep, title: string): Partial<AOPDraftStep> =>
	step.type === 'decision'
		? ({ question: title, description: title.slice(0, AOP_MAX_DESCRIPTION) } as Partial<AOPStep>)
		: ({ description: title.slice(0, AOP_MAX_DESCRIPTION) } as Partial<AOPStep>);

export const AOP_NODE_FALLBACK_WIDTH = 190;
export const AOP_NODE_FALLBACK_HEIGHT = 120;

/**
 * How wide a card may be. The content decides within these; layout reads the
 * measured result back, so nothing downstream assumes a width. The lower bound
 * is `AOP_NODE_FALLBACK_WIDTH` written as a class — a card is never narrower
 * than the guess layout makes before it has been measured.
 */
export const AOP_NODE_BOUNDS_CLASS = 'min-w-[190px] max-w-[260px]';

/* -------------------------------------------------------------------------- */
/*                                 Vocabulary                                 */
/* -------------------------------------------------------------------------- */

export const AOP_PARTIES: { value: AOPParty; label: string; description: string }[] = [
	{ value: 'supplier', label: 'Supplier', description: 'The supplier holding the job.' },
	{ value: 'buyer', label: 'Site contact', description: 'The site or store contact.' },
	{
		value: 'vfa',
		label: 'Virtual facilities assistant',
		description: 'The virtual facilities assistant on duty for this buyer.',
	},
];

export const AOP_OUTCOMES: { value: AOPOutcome; label: string; description: string }[] = [
	{
		value: 'success',
		label: 'Success',
		description: 'The job is on track: somebody attended it or the supplier confirmed it.',
	},
	{ value: 'failure', label: 'Failure', description: 'A person has to pick this up.' },
];

/**
 * How an ending reads at a glance, on the block's flag.
 *
 * Green is `lifecycleChange.negative` because it is the only readable green in
 * the palette — every entry in the `green` scale is a pale fill, the darkest
 * being green-5 at #9cd8b6. The token is named for a cost going down rather
 * than for success, so this still wants a token of its own.
 *
 * The `!` is not decoration. antd's stylesheet loads after Tailwind's and sets
 * `.anticon { color: inherit }`, so without it the icon ignores its own colour
 * and takes the block's group accent instead — which is how every flag came
 * out dark. It is written into the string rather than added at the call site
 * because Tailwind only emits a rule for a class it finds spelled out.
 */
export const outcomeAccent = (outcome: AOPOutcome): string =>
	outcome === 'success' ? '!text-lifecycleChange-negative' : '!text-character-danger';

export const AOP_TRIGGERS: { value: AOPTrigger; label: string; description: string }[] = [
	{
		value: 'note_added',
		label: 'Note added',
		description: 'Somebody wrote a note on the work order.',
	},
	{
		value: 'status_changed',
		label: 'Status changed',
		description: 'The work order moved to a different status.',
	},
];

/**
 * An operation's results are exactly its step's exits — the service rejects a
 * plan whose operate step names any other set.
 */
export const AOP_OPERATIONS: {
	[operation: string]: { label: string; description: string; results: string[] };
} = {
	reassign_supplier: {
		label: 'Assign next supplier',
		description: 'Hand the job to the next supplier down the ranked list.',
		results: ['reassigned', 'supplier_list_exhausted'],
	},
};

/* -------------------------------------------------------------------------- */
/*                                   Palette                                  */
/* -------------------------------------------------------------------------- */

export interface AOPPaletteItem {
	/** Null for an entry that is listed but is not a step you can add. */
	stepType: AOPStepType | null;
	group: AOPPaletteGroup;
	label: string;
	/** Why it cannot be dragged, when it cannot. Shown on the item. */
	unavailable?: string;
}

export const AOP_PALETTE_GROUP_LABELS: { group: AOPPaletteGroup; label: string }[] = [
	{ group: 'lifecycle', label: 'Lifecycle & triggers' },
	{ group: 'dispatch', label: 'Vendor dispatch & assignment' },
	{ group: 'communications', label: 'Communications & alerts' },
	{ group: 'logic', label: 'Logic & branching' },
];

/**
 * The heading a block sits under is what gives it its accent colour.
 *
 * All four are existing design tokens. The design used a blue and a green that
 * the token set has no equivalent for, so dispatch and communications take the
 * nearest hues that do exist rather than introducing new values.
 */
/**
 * The accent per palette group, as class names rather than colour values.
 *
 * Repeating a token's hex here is how a palette drifts from `tailwind.config.js`
 * — and Tailwind only emits a class it can find written out, so these are whole
 * strings and never interpolated. Two maps because a group is used as both a
 * glyph colour and a fill.
 */
export const AOP_GROUP_TEXT_CLASS: { [group: string]: string } = {
	lifecycle: 'text-grey-8',
	dispatch: 'text-teal-2',
	communications: 'text-sunsetOrange-8',
	logic: 'text-purple-6',
};

export const AOP_GROUP_BG_CLASS: { [group: string]: string } = {
	lifecycle: 'bg-grey-8',
	dispatch: 'bg-teal-2',
	communications: 'bg-sunsetOrange-8',
	logic: 'bg-purple-6',
};

/** What kind of step an alternative is, said plainly. */
export const AOP_STEP_TYPE_LABELS: { [type: string]: string } = {
	communicate: 'Sends a work-order note',
	operate: 'Assigns the next supplier',
	decision: 'Asks a question and branches',
	finish: 'Ends the procedure',
};

export const AOP_PALETTE_ITEMS: AOPPaletteItem[] = [
	// Start is listed because the design lists it, but it is not a step: a plan
	// enters at `steps[0]` and what makes Radar pick a work order up belongs to
	// the Scenario. There is exactly one and it is always drawn, so there is
	// nothing to drag.
	{
		stepType: null,
		group: 'lifecycle',
		label: 'Start',
		unavailable: 'Already on the canvas — set its criteria on the Scenarios tab',
	},
	{ stepType: 'finish', group: 'lifecycle', label: 'Finish' },
	{ stepType: 'operate', group: 'dispatch', label: 'Assign next supplier' },
	{ stepType: 'communicate', group: 'communications', label: 'Send communication' },
	{ stepType: 'decision', group: 'logic', label: 'Decision Block' },
];

export const AOP_STEP_GROUPS: { [stepType: string]: AOPPaletteGroup } = {
	finish: 'lifecycle',
	operate: 'dispatch',
	communicate: 'communications',
	decision: 'logic',
};

/**
 * An unsupported step has no palette group — it is not something you can add,
 * it is something the compiler could not express — so it takes the danger
 * colour instead of a group accent.
 */
export const accentTextClassForStep = (stepType: AOPStepType | 'unsupported'): string =>
	stepType === 'unsupported'
		? 'text-character-danger'
		: AOP_GROUP_TEXT_CLASS[AOP_STEP_GROUPS[stepType]];

export const accentBgClassForStep = (stepType: AOPStepType | 'unsupported'): string =>
	stepType === 'unsupported'
		? 'bg-character-danger'
		: AOP_GROUP_BG_CLASS[AOP_STEP_GROUPS[stepType]];

/* -------------------------------------------------------------------------- */
/*                                  Display                                   */
/* -------------------------------------------------------------------------- */

const findLabel = (
	table: { value: string; label: string }[],
	value: string,
	fallback: string
): string => {
	const match = table.filter((entry) => entry.value === value)[0];
	return match ? match.label : fallback;
};

export const partyLabel = (party: AOPParty): string => findLabel(AOP_PARTIES, party, party);

export const outcomeLabel = (outcome: AOPOutcome): string =>
	findLabel(AOP_OUTCOMES, outcome, outcome);

export const outcomeDescription = (outcome: AOPOutcome): string => {
	const match = AOP_OUTCOMES.filter((entry) => entry.value === outcome)[0];
	return match ? match.description : '';
};

export const triggerLabel = (trigger: AOPTrigger): string =>
	findLabel(AOP_TRIGGERS, trigger, trigger);

/** `supplier_list_exhausted` -> `Supplier list exhausted`. */
/**
 * What an exit is called, wherever it is shown.
 *
 * An exit arrives as a bare name — `supplier_list_exhausted`, `on_exhausted` —
 * and three screens used to turn it into words separately: the block drew
 * "Supplier list exhausted", the panel beside it called the same exit "Supplier
 * found", and the activity feed said "no reply in time" where the block said
 * "no response". One exit, three vocabularies.
 *
 * The names the service declares get a phrase here; anything else is an answer
 * the author wrote, so its own words are already the right ones and it is only
 * tidied.
 *
 * Every entry is a noun phrase in sentence case, and it is shown exactly as
 * written — no screen lowercases or re-wraps it. Where that did not fit, the
 * copy around it moved instead: the block's catch-all row leads with `if ·`
 * like every other exit rather than reading as a sentence, and the activity
 * feed puts the exit on its own line rather than inside one.
 */
const EXIT_LABELS: { [name: string]: string } = {
	reassigned: 'Supplier found',
	supplier_list_exhausted: 'No supplier available',
	[AOP_ON_TIMEOUT]: 'No reply in time',
	[AOP_ON_EXHAUSTED]: 'No reply after every reminder',
};

export const exitLabel = (name: string): string =>
	EXIT_LABELS[name] ? EXIT_LABELS[name] : humaniseExitName(name);

export const humaniseExitName = (name: string): string => {
	const words = name.split('_').join(' ');
	return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Seconds as the shortest sensible unit, e.g. 2700 -> "45m", 86400 -> "1d". */
/**
 * A duration, composed rather than reduced to one unit: 6300 reads as
 * "1h 45m", not "105m".
 */
const splitDuration = (seconds: number): { days: number; hours: number; minutes: number } => ({
	days: Math.floor(seconds / 86400),
	hours: Math.floor((seconds % 86400) / 3600),
	minutes: Math.floor((seconds % 3600) / 60),
});

/** Short form, for the exit rows on a card: "1h 45m". */
export const formatSeconds = (seconds: number): string => {
	const split = splitDuration(seconds);
	const parts: string[] = [];

	if (split.days > 0) {
		parts.push(`${split.days}d`);
	}
	if (split.hours > 0) {
		parts.push(`${split.hours}h`);
	}
	if (split.minutes > 0) {
		parts.push(`${split.minutes}m`);
	}

	return parts.length > 0 ? parts.join(' ') : `${seconds}s`;
};

/** Long form, for prose in a tooltip or a panel: "1 hr 45 min". */
export const formatSecondsLong = (seconds: number): string => {
	const split = splitDuration(seconds);
	const parts: string[] = [];

	if (split.days > 0) {
		parts.push(`${split.days} ${split.days === 1 ? 'day' : 'days'}`);
	}
	if (split.hours > 0) {
		parts.push(`${split.hours} hr`);
	}
	if (split.minutes > 0) {
		parts.push(`${split.minutes} min`);
	}

	return parts.length > 0 ? parts.join(' ') : `${seconds} sec`;
};

/**
 * What an escalation does, in a sentence: "Send an SMS to the supplier".
 * Action is the channel, recipient is the party — the two fields an escalation
 * actually has.
 */
export const escalationActionLabel = (escalation: AOPEscalation): string => {
	const channel = AOP_CHANNELS.filter((entry) => entry.value === escalation.channel)[0];
	const action = channel ? channel.label : escalation.channel;

	// Capitalised, as it is everywhere else. A party is a defined term in the
	// plan's vocabulary rather than ordinary prose, so it keeps its shape inside
	// a sentence the way the recipient picker shows it.
	return `${action} to the ${partyLabel(escalation.party)}`;
};

/**
 * One escalation as a sentence, for the Steps panel: what Radar does and how
 * long it then waits.
 */
export const escalationSentence = (escalation: AOPEscalation): string =>
	`${escalationActionLabel(escalation)}. Allow ${formatSecondsLong(
		escalation.window_seconds
	)} for a response.`;

/**
 * When an escalation fires, counted from the moment the work order reaches the
 * decision.
 *
 * A decision has two kinds of wait. `timeout_seconds` is a quiet watch before
 * anybody is contacted at all; each escalation's `window_seconds` is how long
 * Radar waits for an answer after sending that one. So escalation N goes out
 * once the initial wait and every window before it have passed.
 */
export const escalationStartsAfter = (
	timeoutSeconds: number,
	escalations: AOPEscalation[],
	index: number
): number =>
	escalations
		.slice(0, index)
		.reduce((total, escalation) => total + escalation.window_seconds, timeoutSeconds);

/**
 * When the catch-all branch is taken: the whole life of the step, which is the
 * initial wait plus every escalation's window. With no escalations that is just
 * the initial wait, and it genuinely is a give-up time.
 */
export const decisionGivesUpAfter = (step: AOPDecisionStep): number =>
	(step.escalations ? step.escalations : []).reduce(
		(total, escalation) => total + escalation.window_seconds,
		step.timeout_seconds
	);

/* -------------------------------------------------------------------------- */
/*                                   Exits                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether a step says where it goes.
 *
 * Everything but a finish and an unsupported step does: a communicate step
 * carries its single `after` exit, so this no longer means "operate or
 * decision". Nothing continues from a step Radar cannot run, and a finish is
 * the end.
 */
export const stepHasExits = (step: AOPDraftStep): boolean =>
	step.type === 'operate' || step.type === 'decision' || step.type === 'communicate';

export const exitsOf = (step: AOPDraftStep): AOPExit[] =>
	step.type === 'operate' || step.type === 'decision' || step.type === 'communicate'
		? step.exits
		: [];

/**
 * A decision needs exactly one catch-all exit, and which one depends on whether
 * it nudges while it waits: `on_exhausted` once escalations are set, otherwise
 * `on_timeout`. Naming the wrong one is a validation error upstream.
 */
export const catchAllExitName = (escalations: AOPEscalation[]): string =>
	escalations.length > 0 ? AOP_ON_EXHAUSTED : AOP_ON_TIMEOUT;

/**
 * Setting a decision's escalations, with its catch-all exit renamed to match.
 *
 * The two are one fact — `on_exhausted` with nothing to exhaust is a plan the
 * service refuses — so they are written together rather than by each caller
 * remembering to. Adding the first escalation, taking the last one away and
 * switching the branch by hand were three places that each knew the rule, and
 * keeping them in agreement is what the review of this panel was mostly about.
 */
export const withEscalations = (
	step: AOPDecisionStep,
	escalations: AOPEscalation[],
	/** Where a catch-all goes when the step has none and one has to be made. */
	fallbackTarget?: AOPStepName
): Partial<AOPDecisionStep> => {
	const name = catchAllExitName(escalations);
	const held = step.exits.filter((exit) => isCatchAllExit(exit.name))[0];

	/*
	  A decision with no catch-all at all is the state the service reports as
	  `missing_exit` — a compiler answer we could read but not repair, because
	  every control for the no-answer branch needs an exit to act on. One is
	  made here instead, so the first edit of such a step fixes it.
	*/
	const exits = held
		? step.exits.map((exit) => (isCatchAllExit(exit.name) ? { ...exit, name } : exit))
		: step.exits.concat([{ name, target: fallbackTarget ? fallbackTarget : step.name }]);

	return { escalations, exits };
};

/**
 * A name for a new answer that nothing in the list already uses.
 *
 * Counting the answers is not enough on its own: one of them may have been
 * renamed to the name that count would produce, and the service rejects a step
 * naming an exit twice.
 */
export const freeExitName = (exits: AOPExit[]): string => {
	const taken = new Set(exits.map((exit) => exit.name));
	// Count the answers, not the exits: a decision always carries a catch-all
	// too, and including it made the third answer come out as "Answer 4".
	const answers = exits.filter((exit) => !isCatchAllExit(exit.name)).length;

	for (let suffix = answers + 1; suffix < answers + 100; suffix += 1) {
		const candidate = `answer_${suffix}`;
		if (!taken.has(candidate)) {
			return candidate;
		}
	}

	return `answer_${answers + 1}`;
};

export const isCatchAllExit = (name: string): boolean =>
	name === AOP_ON_TIMEOUT || name === AOP_ON_EXHAUSTED;

/**
 * What a block is called, on its card, in the Steps panel and in a branch
 * target picker.
 *
 * A decision carries both a `label` and a `question`, and they say the same
 * thing — "Supplier accepted the work order?" is the name of the block. So the
 * question is the name, and `label` is kept in step with it rather than drifting
 * as a second title nobody edits.
 */
/**
 * The compiler writes labels and questions as instructions to Radar — lowercase
 * and mid-sentence: "get the supplier", "watch for somebody on site". Every
 * screen shows them as names, so every screen capitalises them.
 *
 * Display only. The plan keeps whatever the author and the compiler wrote, and
 * the rename field in the block panel still edits `step.label` itself, so
 * nothing capitalised here is ever written back.
 */
export const sentenceCase = (text: string): string => {
	// Guarded, not because a step should ever be nameless, but because a plan is
	// a document from another service: a field it stops sending must leave the
	// canvas blank rather than throw and take the whole screen with it.
	if (!text) {
		return '';
	}

	const trimmed = text.trim();
	return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

/**
 * What a step is called, everywhere it is named.
 *
 * Every type but `decision` is simply its own `description`. An operate step
 * used to be an exception, titled by its operation instead — which made sense
 * when the field was `label` and the compiler filled it with the example from
 * its own prompt. It is `description` now, a sentence written to be read, so
 * there is nothing left worth overriding, and overriding it put a different
 * title on the block than in the panel that edits it.
 *
 * The operation has not gone anywhere: it is the block's body and the panel's
 * Assignment field. It was only ever the title the two competed for.
 *
 * A decision is its question, because that is what the step asks and its
 * description only restates it.
 */
export const stepTitle = (step: AOPDraftStep): string =>
	sentenceCase(step.type === 'decision' && step.question ? step.question : step.description);

/** The output rows drawn down the right edge of a block. */
export const outputsOf = (step: AOPDraftStep): AOPOutputDef[] => {
	if (step.type === 'decision') {
		return step.exits.map((exit) => {
			if (!isCatchAllExit(exit.name)) {
				return {
					id: exit.name,
					label: humaniseExitName(exit.name),
					condition: '',
					leading: 'if' as 'if',
					title: `Taken when the answer is "${humaniseExitName(exit.name)}"`,
				};
			}

			// The catch-all reads as a sentence rather than a label and a duration:
			// "If no response after escalation (>2h)". The wording comes from the
			// exit's own name, not from whether the step happens to have
			// escalations — the two can disagree, and when they do the service
			// reports it as a finding. Saying what the document says keeps the card
			// honest about the branch that will actually be taken.
			return {
				id: exit.name,
				label: exitLabel(exit.name),
				condition: '',
				// The whole life of the step, not just the initial wait: with
				// escalations the branch is taken once every window has run too.
				suffix: `(>${formatSeconds(decisionGivesUpAfter(step))})`,
				// The same `if ·` every other exit row leads with. It used to read
				// as a sentence — "If no response after escalation" — which meant
				// lowercasing the label, and a label that changes shape by where it
				// is shown is not one label.
				leading: 'if' as 'if',
				title: `Taken when ${formatSeconds(decisionGivesUpAfter(step))} passes with no answer`,
			};
		});
	}

	if (step.type === 'operate') {
		return step.exits.map((exit) => ({
			id: exit.name,
			label: exitLabel(exit.name),
			condition: '',
			leading: 'if' as 'if',
			title: exitLabel(exit.name),
		}));
	}

	/*
	  A communicate step's one exit. It reads as a statement rather than a
	  condition — nothing is being chosen, it is taken as soon as the note is
	  written — so it leads with "then" where the others lead with "if".
	*/
	if (step.type === 'communicate') {
		return step.exits.map((exit) => ({
			id: exit.name,
			label: 'Note written',
			condition: '',
			leading: 'then' as 'then',
			title: 'Taken once the note is on the work order',
		}));
	}

	return [];
};

/* -------------------------------------------------------------------------- */
/*                                 Traversal                                  */
/* -------------------------------------------------------------------------- */

/**
 * The successor map, matching `successors()` upstream exactly: exit targets for
 * steps that have exits, and nothing after a finish or an unsupported step.
 */
export const successorsOf = (plan: AOPDraftPlan): { [stepId: string]: AOPStepName[] } => {
	const edges: { [stepId: string]: AOPStepName[] } = {};

	// Every edge is an exit now. A communicate step carries `after`, so list
	// order no longer decides where anything goes — it is only the order the
	// steps are read in.
	plan.steps.forEach((step) => {
		edges[step.name] = exitsOf(step).map((exit) => exit.target);
	});

	return edges;
};

export const walkFrom = (
	starts: AOPStepName[],
	edges: { [stepId: string]: AOPStepName[] }
): Set<AOPStepName> => {
	const seen = new Set<AOPStepName>();
	const queue = starts.slice();

	while (queue.length > 0) {
		const current = queue.pop();

		if (current === undefined || seen.has(current) || !edges[current]) {
			continue;
		}

		seen.add(current);
		edges[current].forEach((next) => queue.push(next));
	}

	return seen;
};

/* -------------------------------------------------------------------------- */
/*                                 Factories                                  */
/* -------------------------------------------------------------------------- */

/**
 * Step ids are the author-facing handle upstream ("What the step is, used by no
 * other step"), so they are slugs rather than uuids — but they still have to be
 * unique, hence the suffix when a slug is already taken.
 */
export const makeStepName = (description: string, taken: Set<string>): string => {
	const slug = description
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 48);
	// `name` must match `^[a-z][a-z0-9_]*$`, so a description that starts with a
	// digit — "24 hour check" — cannot be slugged straight into one.
	const base = slug && /^[a-z]/.test(slug) ? slug : `step_${slug ? slug : ''}`.replace(/_$/, '');

	if (!taken.has(base)) {
		return base;
	}

	for (let suffix = 2; suffix < 100; suffix++) {
		const candidate = `${base}_${suffix}`;
		if (!taken.has(candidate)) {
			return candidate;
		}
	}

	return `${base}_${uuidv4().slice(0, 8)}`;
};

/**
 * The escalation Action list. Action is the escalation's `channel`, which the
 * service declares as a const `"note"`, and `Escalation` sets
 * `additionalProperties: false` — so this list is the whole of what can be
 * sent. SMS and voice belong here when they exist, and not
 * before, because an option that cannot be chosen is only a reminder that the
 * product is unfinished.
 */
export const AOP_CHANNELS: { value: string; label: string }[] = [
	{ value: 'note', label: 'Add a work-order note' },
];

export const AOP_MAX_ESCALATIONS = 5;

/**
 * A new escalation. `message` is required and must not be empty upstream, so it
 * is seeded rather than left blank — an empty one would fail the draft write
 * and the canvas would lose the edit.
 */
export const createEscalation = (): AOPEscalation => ({
	party: 'supplier',
	channel: 'note',
	message: 'Radar is still waiting for a response on this work order.',
	window_seconds: 15 * 60,
});

/*
 * Named for what it does rather than for the kind of step it is. `note` is the
 * only channel, so the name can say so; the day there are others, the default
 * follows whichever one the block is created with. It is only a default — the
 * description is an ordinary sentence the author can rewrite, and a block Radar
 * compiled arrives with one Radar wrote.
 */
export const createCommunicateStep = (name: string, target: AOPStepName): AOPCommunicateStep => ({
	name,
	description: 'Add work-order note',
	type: 'communicate',
	party: 'supplier',
	channel: 'note',
	message: 'Please confirm dispatch for this work order.',
	// One exit, taken once the note is written. Required upstream: a communicate
	// step no longer falls through to whatever happens to be listed next.
	exits: [{ name: AOP_AFTER_EXIT, target }],
});

export const createOperateStep = (name: string, target: AOPStepName): AOPOperateStep => ({
	name,
	description: 'Assign next supplier',
	type: 'operate',
	operation: 'reassign_supplier' as AOPOperation,
	// Exactly the operation's declared results; any other set is rejected.
	exits: AOP_OPERATIONS.reassign_supplier.results.map((name) => ({ name, target })),
});

export const createDecisionStep = (name: string, target: AOPStepName): AOPDecisionStep => ({
	name,
	// The question and the description say the same thing, so a new block starts
	// with them agreeing rather than with a placeholder nobody edits.
	description: 'Has the supplier confirmed?',
	type: 'decision',
	question: 'Has the supplier confirmed?',
	timeout_seconds: 30 * 60,
	triggers: ['note_added'],
	escalations: [],
	exits: [
		{ name: 'yes', target },
		{ name: 'no', target },
		{ name: AOP_ON_TIMEOUT, target },
	],
});

export const createFinishStep = (name: string): AOPFinishStep => ({
	name,
	description: 'Finish',
	type: 'finish',
	outcome: 'success',
});

/**
 * A new block is wired to `fallbackTarget` on every exit rather than left
 * dangling — an exit's target is required upstream, so there is no such thing
 * as an unconnected exit. The author rewires it by dragging.
 */
export const createStepOfType = (
	stepType: AOPStepType,
	id: string,
	fallbackTarget: AOPStepName
): AOPStep => {
	switch (stepType) {
		case 'communicate':
			return createCommunicateStep(id, fallbackTarget);
		case 'operate':
			return createOperateStep(id, fallbackTarget);
		case 'decision':
			return createDecisionStep(id, fallbackTarget);
		case 'finish':
		default:
			return createFinishStep(id);
	}
};
